/**
 * Gemini Live Agent - Stable Version
 * 
 * Uses a hybrid approach for reliability:
 * - Web Speech API for speech recognition (browser-native, very reliable)
 * - Google Gemini for AI processing (fast and smart)
 * - Browser TTS with enhanced voices for output
 * 
 * This is more stable than the experimental Live API.
 */

import { GoogleGenAI } from "@google/genai";
import { MCPServer } from '../mcp/server/MCPServer';
import { MCPRequest } from '../mcp/types';
import { GeminiTTS, GeminiVoiceName } from './geminiTTS';

const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY || '';

interface GeminiLiveAgentConfig {
  orgId: string;
  userId: string;
  mcpServer: MCPServer;
  language?: 'de' | 'en';
  voice?: GeminiVoiceName;
}

interface VoiceResponse {
  text: string;
  toolCalls?: Array<{
    name: string;
    arguments: any;
    result?: any;
  }>;
}

export class GeminiLiveAgent {
  private config: GeminiLiveAgentConfig;
  private genAI: GoogleGenAI | null = null;
  private recognition: any = null;
  private tts: GeminiTTS | null = null;
  private isListeningActive: boolean = false;
  private responseCallback: ((response: VoiceResponse) => void) | null = null;
  private toolCallCallback: ((toolName: string, args: any) => void) | null = null;
  private transcriptCallback: ((transcript: string, isFinal: boolean) => void) | null = null;
  private statusCallback: ((status: string) => void) | null = null;
  private availableTools: any[] = [];
  private conversationHistory: Array<{ role: 'user' | 'model'; content: string }> = [];
  private isProcessing: boolean = false;

  constructor(config: GeminiLiveAgentConfig) {
    this.config = {
      language: 'de',
      voice: 'Puck', // Upbeat, energetic voice
      ...config
    };
  }

  /**
   * Initialize the agent
   */
  async initialize(): Promise<void> {
    console.log('🎤 Initializing Gemini Voice Agent with Premium TTS...');

    if (!GEMINI_API_KEY) {
      throw new Error('Gemini API Key fehlt. Bitte VITE_GEMINI_API_KEY in .env setzen.');
    }

    // Initialize Gemini client for chat
    this.genAI = new GoogleGenAI({ apiKey: GEMINI_API_KEY });
    console.log('✅ Gemini client initialized');

    // Initialize Gemini TTS with premium voice
    this.tts = new GeminiTTS(this.config.voice || 'Kore');
    await this.tts.initialize();
    console.log(`✅ Gemini TTS initialized with voice: ${this.config.voice || 'Kore'}`);

    // Initialize Web Speech API for recognition
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      throw new Error('Spracherkennung wird nicht unterstützt. Bitte Chrome oder Edge verwenden.');
    }

    const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    this.recognition = new SpeechRecognition();
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = this.config.language === 'en' ? 'en-US' : 'de-DE';
    
    console.log('✅ Speech recognition initialized');

    // Load MCP tools
    await this.loadMCPTools();

    console.log('✅ Gemini Voice Agent ready with Premium TTS!');
  }

  /**
   * Load MCP tools
   */
  private async loadMCPTools(): Promise<void> {
    try {
      const toolsRequest: MCPRequest = {
        jsonrpc: '2.0',
        id: 'gemini-tools',
        method: 'tools/list',
        params: {
          _context: {
            orgId: this.config.orgId,
            userId: this.config.userId
          }
        }
      };

      const response = await this.config.mcpServer.handleRequest(toolsRequest);
      this.availableTools = response.result?.tools || [];
      console.log(`✅ Loaded ${this.availableTools.length} MCP tools`);
    } catch (error) {
      console.error('Error loading MCP tools:', error);
      this.availableTools = [];
    }
  }

  /**
   * Set callbacks
   */
  onResponse(callback: (response: VoiceResponse) => void): void {
    this.responseCallback = callback;
  }

  onToolCall(callback: (toolName: string, args: any) => void): void {
    this.toolCallCallback = callback;
  }

  onTranscript(callback: (transcript: string, isFinal: boolean) => void): void {
    this.transcriptCallback = callback;
  }

  onStatus(callback: (status: string) => void): void {
    this.statusCallback = callback;
  }

  /**
   * Start listening
   */
  async startListening(): Promise<void> {
    if (this.isListeningActive) return;

    this.isListeningActive = true;
    this.statusCallback?.('Ich höre zu...');
    
    let finalTranscript = '';

    this.recognition.onresult = async (event: any) => {
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        
        if (event.results[i].isFinal) {
          finalTranscript = transcript;
          this.transcriptCallback?.(finalTranscript, true);
          
          // Process the final transcript
          if (finalTranscript.trim() && !this.isProcessing) {
            await this.processUserInput(finalTranscript.trim());
            finalTranscript = '';
          }
        } else {
          interimTranscript = transcript;
          this.transcriptCallback?.(interimTranscript, false);
        }
      }
    };

    this.recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'no-speech') {
        // Restart on no-speech
        if (this.isListeningActive) {
          setTimeout(() => {
            try { this.recognition.start(); } catch (e) {}
          }, 100);
        }
      }
    };

    this.recognition.onend = () => {
      if (this.isListeningActive && !this.isProcessing) {
        try { this.recognition.start(); } catch (e) {}
      }
    };

    try {
      this.recognition.start();
      console.log('🎤 Listening started');
    } catch (error) {
      console.error('Error starting recognition:', error);
      throw error;
    }
  }

  /**
   * Stop listening
   */
  async stopListening(): Promise<void> {
    this.isListeningActive = false;
    
    try {
      this.recognition?.stop();
    } catch (e) {}

    // Stop TTS playback
    if (this.tts) {
      this.tts.stop();
    }

    this.statusCallback?.('Gestoppt');
    console.log('🛑 Listening stopped');
  }

  /**
   * Process user input with Gemini
   */
  private async processUserInput(userMessage: string): Promise<void> {
    if (!this.genAI || this.isProcessing) return;

    this.isProcessing = true;
    this.statusCallback?.('Verarbeite...');

    try {
      // Pause recognition while processing
      try { this.recognition?.stop(); } catch (e) {}

      // Add to conversation history
      this.conversationHistory.push({ role: 'user', content: userMessage });

      // Build tool definitions
      const toolInfo = this.availableTools.slice(0, 10).map(t => 
        `${t.name}: ${t.description?.substring(0, 100) || 'No description'}`
      ).join('\n');

      const prompt = `Du bist ein freundlicher CRM Voice Assistant.

VERFÜGBARE TOOLS (nutze sie wenn passend):
${toolInfo}

REGELN:
- Antworte auf Deutsch, kurz und natürlich (max 2-3 Sätze)
- Bei CRM-Anfragen (Leads, Tasks, Opportunities) nutze das Format: [TOOL:name|param1=value1|param2=value2]
- Bei einfachen Fragen (Hallo, wie geht's) antworte direkt

BEISPIELE:
User: "Zeige meine Aufgaben" → [TOOL:list_tasks] Moment, ich schaue nach deinen Aufgaben...
User: "Briefing für Microsoft" → [TOOL:get_customer_briefing|companyName=Microsoft] Ich bereite das Briefing vor...
User: "Hallo" → Hallo! Wie kann ich dir heute helfen?

Konversation:
${this.conversationHistory.slice(-4).map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`).join('\n')}

User: ${userMessage}
Assistant:`;

      const response = await this.genAI.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          temperature: 0.7,
          maxOutputTokens: 300
        }
      });

      let responseText = '';
      try {
        // @ts-ignore - text accessor
        responseText = response.text || '';
      } catch {
        responseText = response.candidates?.[0]?.content?.parts?.[0]?.text || '';
      }

      console.log('🤖 Gemini response:', responseText);

      // Check for tool calls
      const toolMatch = responseText.match(/\[TOOL:([^\]]+)\]/);
      let cleanResponse = responseText.replace(/\[TOOL:[^\]]+\]/g, '').trim();

      if (toolMatch) {
        const toolParts = toolMatch[1].split('|');
        const toolName = toolParts[0];
        const args: any = {};
        
        for (let i = 1; i < toolParts.length; i++) {
          const [key, value] = toolParts[i].split('=');
          if (key && value) args[key] = value;
        }

        console.log(`🔧 Executing tool: ${toolName}`, args);
        this.toolCallCallback?.(toolName, args);

        // Execute tool
        const toolResult = await this.executeMCPTool(toolName, args);
        console.log('📊 Tool result raw:', JSON.stringify(toolResult).substring(0, 500));
        
        // Parse the actual data from MCP response format
        // MCP returns: { content: [{ type: "text", text: "{ success: true, tasks: [...] }" }] }
        let parsedResult: any = toolResult;
        if (toolResult?.content?.[0]?.text) {
          try {
            parsedResult = JSON.parse(toolResult.content[0].text);
            console.log('📊 Parsed result:', JSON.stringify(parsedResult).substring(0, 500));
          } catch (e) {
            console.log('⚠️ Could not parse MCP response, using raw');
          }
        }
        
        // Process result based on tool type for better responses
        let resultSummary = '';
        
        if (toolName === 'list_tasks' && parsedResult?.tasks) {
          const tasks = parsedResult.tasks;
          const openTasks = tasks.filter((t: any) => t.status !== 'completed' && t.status !== 'cancelled');
          const pendingCount = tasks.filter((t: any) => t.status === 'pending').length;
          const inProgressCount = tasks.filter((t: any) => t.status === 'in_progress').length;
          const completedCount = tasks.filter((t: any) => t.status === 'completed').length;
          
          resultSummary = `Du hast ${tasks.length} Tasks gesamt, davon ${openTasks.length} offen (${pendingCount} ausstehend, ${inProgressCount} in Bearbeitung) und ${completedCount} erledigt.`;
          if (openTasks.length > 0 && openTasks.length <= 5) {
            resultSummary += ` Offene Tasks: ${openTasks.map((t: any) => t.title).join(', ')}.`;
          }
          // For counting questions, respond directly
          cleanResponse = resultSummary;
        } else if (toolName === 'list_leads' && parsedResult?.leads) {
          const leads = parsedResult.leads;
          resultSummary = `Du hast ${leads.length} Leads. ${leads.slice(0, 3).map((l: any) => l.name || l.company).join(', ')}${leads.length > 3 ? ' und mehr...' : ''}`;
          cleanResponse = resultSummary;
        } else if (toolName === 'list_opportunities' && parsedResult?.opportunities) {
          const opps = parsedResult.opportunities;
          resultSummary = `Du hast ${opps.length} Opportunities.`;
          cleanResponse = resultSummary;
        } else if (parsedResult?.success) {
          // For create/update operations, generate a friendly response
          const toolResponsePrompt = `Du hast das Tool "${toolName}" für den User ausgeführt.

ORIGINAL-ANFRAGE: ${JSON.stringify(args, null, 2)}
ERGEBNIS: Erfolgreich

Antworte kurz und freundlich auf Deutsch (1 Satz). Nenne den Titel/Namen aus der Anfrage, NIEMALS IDs.`;

          const toolResponse = await this.genAI.models.generateContent({
            model: 'gemini-3-flash-preview',
            contents: toolResponsePrompt,
            config: { 
              temperature: 0.7, 
              maxOutputTokens: 100,
              thinkingConfig: { thinkingLevel: 'low' }
            }
          });

          try {
            // @ts-ignore - text accessor
            cleanResponse = toolResponse.text || 'Erledigt!';
          } catch {
            cleanResponse = toolResponse.candidates?.[0]?.content?.parts?.[0]?.text || 'Erledigt!';
          }
        } else {
          cleanResponse = 'Das habe ich leider nicht gefunden.';
        }
        
        console.log('✅ Final voice response:', cleanResponse);
      }

      // Add to history
      this.conversationHistory.push({ role: 'model', content: cleanResponse });

      // Speak the response
      await this.speak(cleanResponse);

      // Trigger callback
      this.responseCallback?.({ text: cleanResponse });

    } catch (error) {
      console.error('Error processing:', error);
      const errorMsg = 'Entschuldigung, da ist etwas schief gelaufen.';
      await this.speak(errorMsg);
      this.responseCallback?.({ text: errorMsg });
    } finally {
      this.isProcessing = false;
      this.statusCallback?.('Ich höre zu...');
      
      // Resume listening
      if (this.isListeningActive) {
        try { this.recognition?.start(); } catch (e) {}
      }
    }
  }

  /**
   * Execute MCP tool
   */
  private async executeMCPTool(toolName: string, args: any): Promise<any> {
    try {
      const request: MCPRequest = {
        jsonrpc: '2.0',
        id: `tool-${Date.now()}`,
        method: 'tools/call',
        params: {
          name: toolName,
          arguments: args,
          _context: {
            orgId: this.config.orgId,
            userId: this.config.userId
          }
        }
      };

      const response = await this.config.mcpServer.handleRequest(request);
      return response.result || response.error;
    } catch (error) {
      console.error(`Error executing ${toolName}:`, error);
      return { error: String(error) };
    }
  }

  /**
   * Speak text using Gemini Premium TTS
   */
  private async speak(text: string): Promise<void> {
    if (this.tts) {
      await this.tts.speak(text);
    }
  }

  /**
   * Disconnect
   */
  disconnect(): void {
    this.stopListening();
    if (this.tts) {
      this.tts.destroy();
    }
    this.conversationHistory = [];
    console.log('🔌 Agent disconnected');
  }

  /**
   * Check if connected (always true for this implementation)
   */
  get connected(): boolean {
    return true;
  }

  // Legacy method for compatibility
  get isConnected(): boolean {
    return true;
  }
}
